import { toKebabCase } from "@eusoft/webapp-core/utils/String";
import { fixEndDate, fixStartDate, formatCurrency, formatString, formatText, objectEditor, parseDate, required, staticItemsSource } from "@eusoft/webapp-ui";
import { contentInfo } from "@eusoft/webapp-ui/abstraction/IContent";
import { MaterialIcon } from "@eusoft/webapp-ui/components/Icon";
import { chartDatePart, formatDatePart, ReportViewContent, type IReportViewOptions } from "./ReportViewContent";
import { formatDateTime } from "@eusoft/webapp-framework/helpers/Format";
import {
    OrderType,
    PaymentMethod,
    SalePointType,
    TimeUnit,
    type IOrdersReport,
    type IOrdersReportRow,
    type IPrintCommand, PrintCommandType, PrinterAlignment, PrinterTextStyle, PrintImageFormat
} from "../../entities/Commands";
import { merchantItemsSource, productItemsSource, salePointsItemsSource } from "../../helpers/ItemsSources";
import { Authorize } from "../../services/Authorize";
import { context } from "../../services/Context";
import { executeAsync } from "../../services/DataService";
import { WithContext } from "../../services/WithContext";
import 'chartjs-adapter-date-fns';
import { groupBy } from "@eusoft/webapp-core/utils/Array";
import type { ChartType } from "chart.js";
import { registerComponent } from "@eusoft/webapp-core/Component";
import {selectPrinterAsync} from "../../actions";
import {paymentService} from "../../abstraction/IPaymentService";

interface IOrderGroup {
    id: unknown;
    name: string;
}

export class OrderReportPage extends ReportViewContent<IOrdersReport, IOrdersReportRow> {

    constructor() {

        super();

        this.init(OrderReportPage, {

            title: "order-report", 

            chartDimension: "total",

            canPrint: true,
            
            rowFields: {

                "total": {
                    format: formatCurrency,
                },
                "count": {
                    label: formatText("order-count")
                },
            },

            prepareFilter: a => ({
                ...a,
                saleCircuitId: context.saleCircuit?.id,
                toDate: fixEndDate(a.toDate as Date),
                fromDate: fixStartDate(a.fromDate as Date),
            }),

            filterEditor: objectEditor<IOrdersReport>(bld => <>

                {bld.date(a => a.fromDate as Date, {
                    label: "from-date-art",

                })}

                {bld.date(a => a.toDate as Date, {
                    label: "to-date-art",
                })}


                {bld.singleSelector(a => a.orderType, {
                    validators: [required],
                    onChanged: (filter) => {
                        filter.salePointId = undefined;
                    },
                    editor: {
                        itemsSource: staticItemsSource(
                            ["buy", OrderType.Buy],
                            ["charge", OrderType.Charge],
                            ["refund", OrderType.CardRefund],
                        )
                    }
                })}

                {bld.autoComplete(a => a.merchantId, {
                    label: "merchant",
                    onChanged: (filter) => {
                        filter.salePointId = undefined;
                        filter.productId = undefined;
                    },
                    editor: {
                        freeText: false,
                        searchMode: "once",
                        prepareFilter: (f, q) => ({
                            ...f,
                            saleCircuitId: context.saleCircuit?.id
                        }),
                        itemsSource: merchantItemsSource
                    }
                })}

                {bld.autoComplete(a => a.salePointId, {
                    label: "sale-point",
                    editor: {
                        freeText: false,
                        searchMode: "once",
                        prepareFilter: (f, q) => ({
                            ...f,
                            saleCircuitId: context.saleCircuit?.id,
                            merchantId: bld.container.inputValue.merchantId,
                            type: [bld.container.inputValue.orderType == OrderType.Buy ? SalePointType.Sell : SalePointType.Charge]
                        }),
                        itemsSource: salePointsItemsSource
                    }
                })}


                {bld.autoComplete(a => a.productId, {
                    label: "product",
                    visible: a => a.orderType == OrderType.Buy,
  
                    editor: {
                        freeText: false,
                        searchMode: "once",
                        prepareFilter: (f, q) => ({
                            ...f,
                            saleCircuitId: context.saleCircuit?.id,
                            merchantId: bld.container.inputValue.merchantId,
                        }),
                        itemsSource: productItemsSource
                    }
                })}

                {bld.singleSelector(a => a.groupBy, {
                    editor: {
                        emptyItem: formatString("[no-group]"),
                        itemsSource: staticItemsSource(
                            ["sale-point", "SalePoint"],
                            ["merchant", "Merchant"],
                            ["product", "Product"],
                            ["payment-method", "PaymentMethod"],
                        )
                    }
                })}

                {bld.singleSelector(m => m.groupByTime, {
                    validators: [],
                    editor: {
                        emptyItem: formatString("[no-group]"),
                        valuesEquals: (a, b) => (JSON.stringify(a) == JSON.stringify(b)),
                        itemsSource: staticItemsSource(
                            ["15 {minutes}", { unit: TimeUnit.Minute, value: 15 }],
                            ["30 {minutes}", { unit: TimeUnit.Minute, value: 30 }],
                            ["1 {hour}", { unit: TimeUnit.Hour, value: 1 }],
                            ["6 {hours}", { unit: TimeUnit.Hour, value: 6 }],
                            ["1 {day}", { unit: TimeUnit.Day, value: 1 }],
                            ["1 {week}", { unit: TimeUnit.Week, value: 1 }],
                            ["1 {month}", { unit: TimeUnit.Month, value: 1 }],
                            ["1 {year}", { unit: TimeUnit.Year, value: 1 }]
                        )
                    }
                })}
            </>, {
                commitMode: "manual",
                style: "vertical",
                inputField: { style: "filled" },
            })
        } as IReportViewOptions<IOrdersReport, IOrdersReportRow>);
    }

    protected formatGroup(groupBy: string, group: IOrderGroup) {
        if (groupBy == "PaymentMethod")
            return formatString(toKebabCase(PaymentMethod[group.id as number]));
        return group.name ?? formatString("none");
    }

    override updateCharts(filter: IOrdersReport) {

        this.charts = [];

        if (!filter || !this.rows || !this.chartDimension)
            return;

        if (!filter.groupByTime?.value && !filter.groupBy)
            return false;

        if (!filter.groupByTime?.value) {

            if (filter.groupBy) {
                this.charts.push({
                    style: ["auto-height"],
                    config: {
                        type: 'pie',
                        data: {
                            labels: this.rows.map(row => this.formatGroup(filter.groupBy,
                                { name: row.groupName, id: row.groupId })),
                            datasets: [
                                {
                                    label: this.formatFieldLabel(this.chartDimension) as string,
                                    data: this.rows.map(row => row[this.chartDimension])
                                }
                            ]
                        }
                    },
                })
            }
        }
        else {
            if (!filter.groupBy) {
                for (const type of ["line", "bar"] as ChartType[]) {
                    this.charts.push({
                        config: {
                            type: type,
                            data: {

                                datasets: [
                                    {
                                        label: this.formatFieldLabel(this.chartDimension) as string,
                                        data: this.rows.map(row => ({
                                            y: row[this.chartDimension],
                                            x: parseDate(row.fromTime)
                                        })),
                                        tension: 0
                                    }
                                ]
                            },
                            options: {
                                responsive: true,
                                maintainAspectRatio: false,
                                scales: {
                                    x: {
                                        type: "time",
                        
                                        time: {
                                            unit: chartDatePart(filter.groupByTime.unit)
                                        }
                                    }
                                }
                            }
                        }
                    })
                }

            }
            else {

                const timeGrps = groupBy(this.rows, a => ({
                    name: a.groupName,
                    id: a.groupId
                } as IOrderGroup));

                for (const type of ["line", "bar"] as ChartType[]) {
                    this.charts.push({
                        config: {
                            type: type,
                            data: {

                                datasets: timeGrps.map(grp => (
                                    {
                                        label: this.formatGroup(filter.groupBy, grp.key),
                                        data: grp.values.map(row => ({
                                            y: row[this.chartDimension],
                                            x: parseDate(row.fromTime)
                                        })),

                                        tension: 0
                                    }
                                ))
                            },
                            options: {
                                responsive: true,
                                maintainAspectRatio: false,
                                scales: {
                                    x: {
                                        type: "time",
                       
                                        time: {
                                            unit: chartDatePart(filter.groupByTime.unit)
                                        }
                                    }
                                }
                            }
                        }
                    })
                }             
            }
        }

        return true;
    }

    override async printResultsAsync(): Promise<void> {
        if(!this.rows || this.rows.length == 0)
            return;
        
        const commands = [
            {
                type: PrintCommandType.PrintImage,
                text: "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",
                alignment: PrinterAlignment.Center,
                textStyle: PrinterTextStyle.Normal,
                format: PrintImageFormat.Png,
                height: 150,
                width: 150
            },
            {
                type: PrintCommandType.PrintText,
                text: "Pay App\n",
                fontSize: 30,
                alignment: PrinterAlignment.Center,
                textStyle: PrinterTextStyle.Bold,
            }
        ] as IPrintCommand[];
        
        let footerCommands = [
            {
                type: PrintCommandType.PrintText,
                text: `\n${formatDateTime(new Date())}`,
                fontSize: 24,
                alignment: PrinterAlignment.Center,
                textStyle: PrinterTextStyle.Normal,
            },
            {
                type: PrintCommandType.PrintText,
                text: "\nwww.pay-app.it",
                fontSize: 24,
                alignment: PrinterAlignment.Center,
                textStyle: PrinterTextStyle.Normal,
            },
            {
                type: PrintCommandType.ScrollPaper,
                linesToScroll: 3,
                alignment: PrinterAlignment.Center,
                textStyle: PrinterTextStyle.Normal,
            }
        ] as IPrintCommand[]
        
        for(const row of this.rows) {
            const groupName = this.formatGroup(this.filterEditor.value.groupBy, { name: row.groupName, id: row.groupId })
            const title = `\n${formatText("total")} ${groupName}`;
            commands.push(
              {
                  type: PrintCommandType.PrintText,
                  text: title,
                  fontSize: 26,
                  alignment: PrinterAlignment.Left,
                  textStyle: PrinterTextStyle.Bold,
              } as IPrintCommand)
            commands.push(
              {
                  type: PrintCommandType.PrintText,
                  text: formatCurrency(row.total),
                  fontSize: 26,
                  alignment: PrinterAlignment.Right,
                  textStyle: PrinterTextStyle.Normal,
              } as IPrintCommand)
        }
        
        commands.push(...footerCommands);
        
        const printer = await selectPrinterAsync();

        paymentService.print(printer.model, commands);        
    }

    override async queryAsync(filter: IOrdersReport) {

        if (filter.groupByTime?.value) {

            if (filter.groupBy) {
                this.groupBy = a => a.fromTime;
                this.formatGroupHeader = (a: Date) => formatDatePart(filter.groupByTime.unit, a);
                this.rowHeader = r => this.formatGroup(filter.groupBy, { name: r.groupName, id: r.groupId }) 
            }
            else {
                this.groupBy = a => "";
                this.rowHeader = r => formatDatePart(filter.groupByTime.unit, r.fromTime);
            }
        }
        else {
            this.rowHeader = r => this.formatGroup(filter.groupBy, { name: r.groupName, id: r.groupId }) 
            this.groupBy = null;
        }
  

        this.groupLabel = filter.groupBy ? formatText(toKebabCase(filter.groupBy)) : formatText("time-label");

        return (await executeAsync("OrdersReport", filter)).rows;
    }

    static override info = contentInfo({
        name: "order-report",
        route: "/reports/order",
        icon: <MaterialIcon color="#00BCD4" name="shopping_cart" />,
        features: [ 
            Authorize(a => a.businessAccount().resource("Report", "read")),
            WithContext("sale-circuit")
        ],
        factory: () => new OrderReportPage()
    });
}

registerComponent(OrderReportPage, "OrderReportPage");

